import { useCallback, useEffect, useState } from "react";
import { GridColumn, GridColumnMenuProps } from "@progress/kendo-react-grid";
import { useLocation } from "react-router";

import { FontIcon, Link } from "@fluentui/react";
import { filter, forEach } from "lodash";
import NextGenKendoGrid from "../components/shared/grid/nextgenkendogrid";
import { FilterMenu } from "../components/shared/grid/gridfilter";
import { GridColumnMenuFilterBaseProps } from "@progress/kendo-react-grid/dist/npm/interfaces/GridColumnMenuFilterBaseProps";
import { Grid } from "@fluentui/react-northstar";
import { getEntityDataFromSession, getRuleValidationMessage } from "../components/shared/common/util";
import { SessionStorageConsts } from "../models/SessionStorageConsts";
import { MessageTypeText } from "../models/MessageTypeText";
import { UIControlsText } from "../content/UIControlsText";

//constants used in accessibility fixes
export const PageNames = {
  Dashboard: "Dashboard",
  NewCaseSubmission: "New Case Submission",
  Discount: "Discount",
};

// Custom Hook Intended to Facilitate Column Filter Menu Close and Focus Handling
export function useFilterMenuEscCloseAndFocus(props?: GridColumnMenuProps) {
  let filterMenuContainerSelector = ".k-grid-columnmenu-popup";

  const escapeFilterMenuHandler = useCallback(
    (event) => {
      if (event.key === "Escape") {
        let isFocusOnfilterMenuDropdown = document.querySelector(':focus')?.classList.contains('k-dropdown-wrap');
        if (!isFocusOnfilterMenuDropdown && props?.onCloseMenu) {
          props.onCloseMenu();
        }
      } else if (event.key === "Tab") {
        let filterMenuContainer = document.querySelector(
          filterMenuContainerSelector
        );
        let primaryFilterButtonDisabledArray = Array.from(
          document.querySelectorAll("button.k-button[disabled]")
        );
        let primaryFilterButtonDisabled = primaryFilterButtonDisabledArray
          ? (primaryFilterButtonDisabledArray[0] as HTMLButtonElement)
          : undefined;
        let filterMenuButtons = Array.from(
          document.querySelectorAll("button.k-button")
        );
        let hasFilterMenuButtons =
          filterMenuButtons?.length === 2 &&
          filterMenuButtons[0] &&
          filterMenuButtons[1];
        if (
          hasFilterMenuButtons &&
          ((primaryFilterButtonDisabled &&
            event.target === filterMenuButtons[0]) ||
            (!primaryFilterButtonDisabled &&
              event.target === filterMenuButtons[1]))
        ) {
          (filterMenuContainer as HTMLDivElement).focus();
        }
      }
    },
    [props]
  );

  useEffect(() => {
    if (props) {
      document
        .querySelector(filterMenuContainerSelector)
        ?.addEventListener("keydown", escapeFilterMenuHandler);

      return () => {
        document
          .querySelector(filterMenuContainerSelector)
          ?.removeEventListener("keydown", escapeFilterMenuHandler);
      };
    }
  }, [props, escapeFilterMenuHandler]);
}

// useToHandleStateAndSetFocusBetweenChannelGridLinksAndViewReseller
export function useToHandleGlassFravcr() {
  let location = useLocation<any>();
  let whereTo = location?.state?.mdppAccessibilityNavigation;

  let pivot = undefined;
  if (
    whereTo === "back to channel tab" &&
    location.pathname.includes("/managecase/cpstrategic")
  ) {
    pivot = "Channel";
  }

  return pivot;
}

export function useToHandleSettingViewResellerNavState(): string | undefined {
  let location = useLocation<any>();
  let currentPath = location.pathname;

  if (currentPath.includes("/managecase/cpstrategic")) {
    return "from channel tab grid link";
  }

  return undefined;
}

export function useToSetFocusOnResellerNameLinkFocus() {
  let currentLocation = useLocation<any>();
  let currentLocationStateLinkId =
    "#" + currentLocation.state?.mdppAccessibilityLinkId;

  useEffect(() => {
    if (currentLocationStateLinkId) {
      (
        document.querySelector(currentLocationStateLinkId) as HTMLElement
      )?.focus();

      let locationState = currentLocation.state;
      let locationNav = locationState?.mdppAccessibilityNavigation;

      if (locationNav && locationNav === "back to channel tab") {
        currentLocation.state.mdppAccessibilityNavigation = "on channel tab";
      }
    }
  }, []);
}

export function MdppAccessibilityLink({
  ...rest
}: {} & React.ComponentPropsWithoutRef<typeof Link>) {
  useToSetFocusOnResellerNameLinkFocus();
  return <Link {...rest}></Link>;
}

//Accessibility - set Focus to Header in View Resellers component
export function useToSetFocusToHeader() {
  useEffect(() => {
    var heading = document.querySelector("h1");
    heading?.focus();
  });
}

//Accessibility - enable validation message to be announced by screen readers
export function useToAddRoleAlert() {
  useEffect(() => {
    var messageBox = document.querySelector(".ms-MessageBar-text");
    messageBox?.setAttribute("role", "alert");
  });
}

//Accessibility - enable Loading message to be announced by screen readers
export function useToSetRoleToLoadingMessage() {
  useEffect(() => {
    var loadingMessageSpinner = document.querySelector(".ms-Spinner-label");
    loadingMessageSpinner?.setAttribute("role", "alert");
  });
}

//Function to modify page title based on the active Page
function setTitle(currentTitle: string) {
  var setDocumentTitle = "NextGen Pricing Portal " + currentTitle;
  var currentDocumentTitle = document.querySelector("head title");
  if (currentDocumentTitle != null) {
    currentDocumentTitle.innerHTML = setDocumentTitle;
  }
}

//Accessibility Fix for screen readers to announce dashboard and new case submissionpage title correctly
export function useToSetPageTitle(page: string) {
  useEffect(() => {
    var currentPage = document.querySelectorAll(
      ".ms-Link.ms-Breadcrumb-itemLink div div"
    );
    if (page == "Discount") {
      var currentPageTitle = currentPage[0]?.innerHTML;
    } else {
      var currentPageTitle = currentPage[1]?.innerHTML;
    }
    setTitle(currentPageTitle);
  });
}

//Accessibility Fix for screen readers to announce Health and Support Page title correctly
export function useToSetSupportPageTitle() {
  useEffect(() => {
    var currentPage = document.querySelectorAll(".DRStyleExpand-197 div div");
    var currentPageTitle = currentPage[0]?.innerHTML;
    setTitle(currentPageTitle);
  });
}

/**
 * Accessibility fix adding role to grid column filter button
 * @function useToRemoveInvaidChildRole
 * @description This function removes the invalid role attribute
 * (Element has children which are not allowed: [role=navigation], button[aria-disabled], button[tabindex], [role=group]) from the child element of the grid
 * valid child elements can have the following aria attributes:
 * ("role=row","role=rowgroup","role=gridcell","role=columnheader","role=rowheader","aria-colindex","aria-rowindex","aria-selected","aria-level","aria-expanded")
 * and role[listbox] (Element has children which are not allowed: span[aria-hidden])
 * This fixes an accessibility issue reported by Accessibility Insights for Web 2.40.0 (axe-core 4.7.2).
 * @see WCAG 1.3.1: Ensures elements with an ARIA role that require child roles contain them (.k-grid)
 * @see https://accessibilityinsights.io/info-examples/web/aria-required-children)
 */
export function useToRemoveInvalidChildRole() {
  useEffect(() => {
    var grid = document.querySelector(".k-grid");

    var listboxes = document.querySelectorAll("[role=listbox]");
    let observers: MutationObserver[] = [];
    listboxes?.forEach((listbox) => {
      listbox.setAttribute("role", "combobox");
      listbox.querySelectorAll("span.k-input[role=option]").forEach((span) => {
        span.setAttribute("aria-label", "option");

        const callback = function (mutationsList: any[], observer: any) {
          for (const mutation of mutationsList) {
            if (
              mutation.type === "attributes" &&
              span.getAttribute("role") === "option"
            ) {
              span.setAttribute("role", "textbox");
            }
          }
        };

        // observer will be used to observe the span element for any changes in the role attribute
        // if the role attribute is changed to option, it will be changed back to textbox
        // this is to fix the accessibility issue reported by Accessibility Insights for Web 2.40.0 (axe-core 4.7.2)
        // the disconnect is not being used on the observer because there's one or more scripts that change the span role back to option
        // the observer should be garbage collected
        const observer = new MutationObserver(callback);
        observer.observe(span, { attributes: true });
        observers.push(observer);
      });
    });

    return () => {  
      forEach(observers, (observer) => {
        observer.disconnect();
      });
    }
  }, []);
}

/**
 * Ensures the Kendo pager "items per page" control exposes an accessible name.
 * @function useToEnsurePagerPageSizeAccessibleName
 * @description The pager page-size DropDownList renders its selected value as
 * <span class="k-input">10</span>, and other accessibility fixes force role="textbox" /
 * role="combobox" on it (to satisfy aria-required-children on the surrounding listbox). A
 * textbox/combobox requires an accessible name, but this value element has none, so Accessibility
 * Insights FastPass fails "aria-input-field-name" (WCAG 4.1.2). This hook continuously gives the
 * pager page-size value element an accessible name (and replaces the placeholder "option" label a
 * prior fix may have left behind), so the rule passes regardless of render/observer timing.
 *
 * It also announces the numeric pager pages as buttons: Kendo renders each page as an anchor
 * (<a href="#" class="k-link">1</a>) inside <ul class="k-pager-numbers">, so NVDA announces them
 * as "link". These controls change the current page in place rather than navigating to a URL, so
 * their correct semantic is a button. Setting role="button" (and aria-current="page" on the
 * selected page) makes NVDA announce "button" without changing keyboard/click activation.
 * @see WCAG 4.1.2: Ensure every ARIA input field has an accessible name (aria-input-field-name)
 * @see WCAG 4.1.2: Name, Role, Value — expose the correct role for interactive controls.
 */
export function useToEnsurePagerPageSizeAccessibleName() {
  useEffect(() => {
    const PAGE_SIZE_LABEL = "Items per page";
    const ensureAccessibleName = () => {
      document
        .querySelectorAll(
          ".k-pager .k-dropdown-wrap .k-input, .k-pager [role='textbox'], .k-pager [role='combobox']",
        )
        .forEach((el) => {
          const element = el as HTMLElement;
          const role = element.getAttribute("role");
          const isNamedInput =
            role === "textbox" ||
            role === "combobox" ||
            element.classList.contains("k-input");
          const currentLabel = element.getAttribute("aria-label");
          if (
            isNamedInput &&
            !element.getAttribute("aria-labelledby") &&
            (!currentLabel || currentLabel === "option")
          ) {
            element.setAttribute("aria-label", PAGE_SIZE_LABEL);
          }
        });

      // Announce numeric pager pages as buttons (NVDA otherwise reads them as "link").
      document
        .querySelectorAll(".k-pager-numbers .k-link")
        .forEach((el) => {
          const anchor = el as HTMLElement;
          if (anchor.getAttribute("role") !== "button") {
            anchor.setAttribute("role", "button");
          }
          if (anchor.classList.contains("k-state-selected")) {
            anchor.setAttribute("aria-current", "page");
          } else if (anchor.getAttribute("aria-current")) {
            anchor.removeAttribute("aria-current");
          }
        });
    };

    ensureAccessibleName();

    const observer = new MutationObserver(ensureAccessibleName);
    observer.observe(document.body, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ["role", "class"],
    });

    return () => {
      observer.disconnect();
    };
  }, []);
}

/**
* Accessibility fix adding role, aria-label, tabindex and keyboard activation to grid column filter button
* @function useToAddRoleToGridColumnFilterButton
* @description This function adds role="button", aria-label="Filter by {column name}", tabindex="0"
* and keyboard activation (Enter/Space) to the parent element of each grid column filter icon,
* to ensure that ARIA attributes are allowed for an element's role, accessible names are present,
* and the element is keyboard focusable and operable.
* This fixes an accessibility issue reported by Accessibility Insights for Web 2.40.0 (axe-core 4.7.2).
*
* The label derivation and attribute application run again whenever the grid header changes
* (column reorder/hide via "Choose Columns", sorting, filtering or async data load re-renders the
* header cells). A run-once effect left stale aria-labels on reused header DOM nodes, so a column
* that moved into another column's position kept the previous column's "Filter by ..." label
* (MAS 4.1.2 - Name, Role, Value).
* @see WCAG 4.1.2: Ensures ARIA attributes are allowed for an element's role (div[aria-label="null filter"])
* @see WCAG 4.1.2: Ensures every ARIA button, link and menuitem has an accessible name
* @see WCAG 2.1.1: Ensures keyboard operability for all interactive elements
* @see https://accessibilityinsights.io/info-examples/web/aria-allowed-attr
*/
export function useToAddRoleToGridColumnFilterButton(){
    useEffect(() => {
        const boundAttr = 'data-a11y-filter-key-bound';

        const getColumnTitle = (columnHeader: Element | null): string => {
            if (!columnHeader) {
                return '';
            }
            // Prefer the explicit title attribute set on the header link (exact column title,
            // without the mandatory "*" marker), then fall back to visible text.
            const linkWithTitle = columnHeader.querySelector('.k-link[title]');
            const titleAttr = linkWithTitle?.getAttribute('title')?.trim();
            if (titleAttr) {
                return titleAttr;
            }
            const columnTitleSpan = columnHeader.querySelector('.k-column-title');
            const columnTitleText = columnTitleSpan?.textContent?.trim();
            if (columnTitleText) {
                return columnTitleText;
            }
            const linkText = columnHeader.querySelector('.k-link')?.textContent?.replace(/\*+$/, '').trim();
            if (linkText) {
                return linkText;
            }
            return columnHeader.textContent?.replace(/\*+$/, '').trim() || '';
        };

        const applyLabels = () => {
            const gridColumnFilterButtons = document.querySelectorAll('.k-icon.k-i-more-vertical');
            gridColumnFilterButtons.forEach((filterIcon) => {
                const filterButton = filterIcon.parentElement as HTMLElement | null;
                if (!filterButton) {
                    return;
                }
                filterButton.setAttribute('role', 'button');
                filterButton.setAttribute('tabindex', '0');

                const columnHeader = filterButton.closest('th[role="columnheader"]');
                const columnTitle = getColumnTitle(columnHeader);
                if (columnTitle) {
                    const expectedLabel = `Filter by ${columnTitle}`;
                    // Re-derive on every header change so a reordered/re-rendered column never
                    // keeps another column's stale aria-label.
                    if (filterButton.getAttribute('aria-label') !== expectedLabel) {
                        filterButton.setAttribute('aria-label', expectedLabel);
                    }
                }

                if (filterButton.getAttribute(boundAttr) !== 'true') {
                    filterButton.setAttribute(boundAttr, 'true');
                    filterButton.addEventListener('keydown', (event: KeyboardEvent) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            filterButton.click();
                        }
                    });
                }
            });
        };

        applyLabels();

        // Kendo re-renders header cells on sort/filter/reorder/data load; re-apply so labels
        // always match the currently displayed column.
        let scheduled = false;
        const observer = new MutationObserver(() => {
            if (scheduled) {
                return;
            }
            scheduled = true;
            window.requestAnimationFrame(() => {
                scheduled = false;
                applyLabels();
            });
        });
        observer.observe(document.body, { childList: true, subtree: true });

        return () => {
            observer.disconnect();
        };
    }, []);
} 

/*
* Accessibility fix to remove invalid role attributes from the child elements of the grid
* @function useToFixGridAriaRequiredChildren
* @description This function removes the invalid role attribute
* (Element has children which are not allowed: [role=navigation], button[aria-disabled], button[tabindex], [role=group]) from the child element of the grid
* valid child elements can have the following aria attributes:
* ("role=row","role=rowgroup","role=gridcell","role=columnheader","role=rowheader","aria-colindex","aria-rowindex","aria-selected","aria-level","aria-expanded")
* and role[listbox] (Element has children which are not allowed: span[aria-hidden])
* This fixes an accessibility issue reported by Accessibility Insights for Web 2.40.0 (axe-core 4.7.2).
* @see WCAG 1.3.1: Ensures elements with an ARIA role that require child roles contain them (.k-grid)
*/
export function useToFixGridAriaRequiredChildren(gridContainerId: string){
  useEffect(() => {
    let gridContainer = document.querySelector(gridContainerId);
    var grid = document.querySelector('.k-grid');
    var gridPager = grid?.querySelectorAll(".k-grid .k-grid-pager");
    gridPager?.forEach((pager) => {
      let removedPager = grid?.removeChild(pager);
      gridContainer?.appendChild(removedPager as Node);
    });

  }, []);
}

/*
* Accessibility fix for the Kendo Grid pager violating aria-required-children (WCAG 1.3.1).
* @function useToFixGridInvalidChildRoles
* @description An element with role="grid" may only own row/rowgroup children, but the Kendo
* Grid renders its pager (.k-pager-wrap, containing nav links a[href], the page-number list
* and the page-size dropdown) INSIDE the grid, and, when empty, a "No records available" row
* with role="alert". The pager cannot be turned into a valid grid child with attributes alone
* because its controls are <a href> links, for which ARIA does not permit grid/row/gridcell
* roles (aria-allowed-role). Following Telerik's own guidance ("the element with role=grid must
* not include the Pager"), this relocates each grid's pager to be an immediate sibling AFTER
* the grid, so role="grid" only owns valid children. React reuses the same pager node across
* re-renders (so this does not create duplicates); a MutationObserver re-applies the move after
* re-renders/remounts and a guard removes any stale relocated pager to stay duplicate-free.
* The pager's page-size dropdown (Kendo renders role="listbox" on .k-dropdown-wrap) is then
* given an accessible name and a valid option child so it passes aria-input-field-name and
* aria-required-children once it is no longer owned by the grid. React events keep working
* because only the DOM node is moved; the React fiber tree is unchanged.
* Also removes the invalid role="alert" from the no-records row, which remains inside the grid.
* @see WCAG 1.3.1: Ensures elements with an ARIA role contain only their required/allowed children
* @see https://www.telerik.com/kendo-react-ui/components/grid/accessibility/wai-aria-support
* @see https://accessibilityinsights.io/info-examples/web/aria-required-children
*/
export function useToFixGridInvalidChildRoles(){
  useEffect(() => {
    const fixInvalidChildRoles = () => {
      document.querySelectorAll('.k-grid[role="grid"]').forEach((grid) => {
        const gridEl = grid as HTMLElement;

        // Remove the invalid role="alert" from the no-records row inside the grid.
        gridEl.querySelectorAll('[role="alert"]').forEach((alert) => {
          alert.removeAttribute("role");
        });

        // Relocate the pager out of the grid so role="grid" only owns row/rowgroup children.
        if (!gridEl.parentElement) {
          return;
        }
        const insidePager = gridEl.querySelector(":scope > .k-pager-wrap") as HTMLElement | null;
        if (insidePager) {
          // Drop any previously relocated pager (from a prior grid mount) to avoid duplicates.
          const relocated = gridEl.nextElementSibling as HTMLElement | null;
          if (
            relocated &&
            relocated.classList.contains("k-pager-wrap") &&
            relocated.getAttribute("data-a11y-relocated") === "true"
          ) {
            relocated.remove();
          }
          insidePager.setAttribute("data-a11y-relocated", "true");
          gridEl.insertAdjacentElement("afterend", insidePager);
        }
      });

      // Fix the pager page-size dropdown. Kendo renders role="listbox" on the collapsed
      // .k-dropdown-wrap anchor, which (1) has no accessible name (aria-input-field-name,
      // WCAG 4.1.2) and (2) exposes no option/group child (aria-required-children, WCAG 1.3.1)
      // because the actual options live in a separate popup. Give it an accessible name and
      // mark its inner selected-value span as the required option child (parent listbox is the
      // wrap itself, so no orphaned option). Mirrors the existing dashboard grid fix.
      document.querySelectorAll(".k-pager .k-dropdown-wrap").forEach((wrap) => {
        const wrapEl = wrap as HTMLElement;
        if (!wrapEl.getAttribute("aria-label")) {
          wrapEl.setAttribute("aria-label", "Select items per page");
        }
        const selected = wrapEl.querySelector(":scope > .k-input") as HTMLElement | null;
        if (selected && selected.getAttribute("role") !== "option") {
          selected.setAttribute("role", "option");
        }
      });
    };

    fixInvalidChildRoles();

    const observer = new MutationObserver(() => {
      fixInvalidChildRoles();
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
    };
  }, []);
}

/**
 * Removes the duplicate/roving tab stop from sortable grid column header cells.
 * @function useToRemoveGridHeaderRovingTabStop
 * @description Kendo's navigatable grid keeps a roving tab order across all cells and puts
 * tabindex="0" on the currently active header <th role="columnheader">. Our custom header renders
 * its own focusable sort control (span[role="button"][tabindex="0"]) inside that <th>, so the
 * active header ends up with TWO tab stops: the generic <th> (which NVDA announces as an extra
 * "section") and the button. This removes the <th> from the tab order (tabindex="-1") whenever it
 * contains our sort button, so Tab moves directly button-to-button (column header to column
 * header) with no spurious "section" announcement. The grid stays navigatable for body/action
 * cells, so only header cells are affected. (MAS 2.4.3 Focus Order / 4.1.2 Name, Role, Value.)
 */
export function useToRemoveGridHeaderRovingTabStop() {
  useEffect(() => {
    const removeHeaderTabStop = () => {
      document
        .querySelectorAll('.k-grid-header th[role="columnheader"]')
        .forEach((th) => {
          const headerCell = th as HTMLElement;
          const hasSortButton = headerCell.querySelector(
            '[role="button"][tabindex]',
          );
          if (hasSortButton && headerCell.getAttribute("tabindex") !== "-1") {
            headerCell.setAttribute("tabindex", "-1");
          }
        });
    };

    removeHeaderTabStop();

    const observer = new MutationObserver(removeHeaderTabStop);
    observer.observe(document.body, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ["tabindex"],
    });

    return () => {
      observer.disconnect();
    };
  }, []);
}

/**
 * Ensures all ARIA attributes have valid values (tr[aria-rowindex="NaN"])
 * @function useToFixAriaAttributes
 * @description This function fixes an accessibility issue where the ARIA attribute value is invalid (aria-rowindex="NaN") for the specified element path (tr[aria-rowindex="NaN"]). The function sets the ARIA attribute value to a valid value.
 * @see WCAG 4.1.2: Ensures all ARIA attributes have valid values (https://www.w3.org/WAI/WCAG21/Understanding/aria-valid-attr-value.html)
 * @see https://accessibilityinsights.io/info-examples/web/aria-valid-attr-value
 */
export function useToFixAriaRowIndexNan() {

  function removeAriaRowIndex(element: Element) {
    if (element.getAttribute("aria-rowindex") === "NaN") {
      element.removeAttribute("aria-rowindex");
    }
  }
  
  useEffect(() => {
    let nanRowIndexes = document.querySelectorAll("tr[aria-rowindex]");
    let observers: MutationObserver[] = [];
    forEach(nanRowIndexes, (element, i) => {
      // once for each element
      removeAriaRowIndex(element);
      const mutationHandler = () => {
        // again in case another script readds the attribute
        removeAriaRowIndex(element);
      }

      const observer = new MutationObserver(mutationHandler);
      observer.observe(element, { attributes: true, attributeFilter: ["aria-rowindex"] });
      observers.push(observer);
    });
    
    return () => {  
      forEach(observers, (observer) => {
        observer.disconnect();
      });
    }
  });
}

/*
* Accessibility fix for case action buttons at reflow resolution
* @function useToShowActionButtonTextOnHoverOrFocusInReflow
* @description This function hides the action button icons and shows the action button text on hover or focus in reflow resolution
* This fixes an accessibility issue reported by manual testing.
*/
export function useToShowActionButtonTextOnHoverOrFocusInReflow() {
  useEffect(() => {
    let actionButtons = document.querySelectorAll(".ms-Stack button.ms-Button--commandBar");
    let actionButtonText = document.querySelectorAll(".ms-Stack button.ms-Button--commandBar span span");
    let actionButtonIcons = document.querySelectorAll(".ms-Stack button.ms-Button--commandBar span i");

    let hideHandler = (event: Event) => {
      if(window.matchMedia("(width < 1281px)").matches){
        let hideAll = (ele: Element) => {
          let htmlEle = ele as HTMLElement;
          htmlEle.style.display = 'none';
        }
        
        // hide each of the button icons
        actionButtonIcons.forEach(hideAll);
        
        // show the current button's text
        let currentButton = event.currentTarget as HTMLElement;
        let currentButtonText = currentButton?.querySelector("span span") as HTMLElement;
        currentButtonText.style.display = 'block';
      }
    }

    let removeDisplayProperty = (element: Element) => {
      let htmlElement = element as HTMLElement;
      htmlElement.style.removeProperty("display");
    }

    let focusSlashMouseOutHandler = () => {
      actionButtonText.forEach(removeDisplayProperty);
      actionButtonIcons.forEach(removeDisplayProperty);
    }

    let focusSlashMouseInHandler = (element: any) => {
      hideHandler(element);
    }
    
    actionButtons.forEach((actionButton) => {
      actionButton.addEventListener("mouseover", focusSlashMouseInHandler);
      actionButton.addEventListener("focusin", focusSlashMouseInHandler);
      actionButton.addEventListener("mouseout", focusSlashMouseOutHandler);
     actionButton.addEventListener("focusout", focusSlashMouseOutHandler);
    });
  }, []);
}

/**
 * Accessibility fix providing an accessible name for the lookup panel grid's filter-row text boxes.
 * @function useToAddAriaLabelToPanelGridFilterTextbox
 * @description Kendo renders each column filter cell in the grid's filter row as a bare
 * <input class="k-textbox"> with no label, so Accessibility Insights / axe report a WCAG 4.1.2
 * "label" failure and NVDA announces the field as "edit blank". This hook derives each filter
 * text box's accessible name from its own column header (matched by column index within the grid's
 * header, so it stays correct regardless of column configuration/visibility) and applies it as an
 * aria-label. Kendo re-renders the filter row on data/filter changes (wiping the attribute), so it
 * is re-applied through a MutationObserver, mirroring the other filter-accessibility hooks. Only a
 * presentational ARIA attribute is added, so Kendo's filter behaviour is unaffected.
 * @see WCAG 4.1.2: Name, Role, Value (https://www.w3.org/WAI/WCAG21/Understanding/name-role-value.html)
 */
export function useToAddAriaLabelToPanelGridFilterTextbox() {
  useEffect(() => {
    const labelFilterTextboxes = () => {
      document.querySelectorAll(".k-grid .k-filter-row").forEach((filterRow) => {
        // The header row immediately precedes the filter row in the header thead, so its cells
        // align 1:1 by column index with the filter cells (including any leading select column).
        const headerCells = (filterRow.previousElementSibling?.children ?? []) as unknown as HTMLCollection;
        Array.from(filterRow.children).forEach((filterCell, index) => {
          const textbox = filterCell.querySelector(".k-filtercell-wrapper > .k-textbox, .k-filtercell .k-textbox");
          if (!textbox) {
            return;
          }
          const existingLabel = textbox.getAttribute("aria-label");
          if (existingLabel && existingLabel.trim()) {
            return;
          }
          const headerCell = headerCells[index];
          const columnTitle = (
            headerCell?.querySelector(".k-column-title")?.textContent ||
            headerCell?.textContent ||
            ""
          ).trim();
          if (columnTitle) {
            textbox.setAttribute("aria-label", columnTitle);
          }
        });
      });
    };

    labelFilterTextboxes();

    const observer = new MutationObserver(() => {
      labelFilterTextboxes();
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["aria-label", "class", "value"],
    });

    return () => {
      observer.disconnect();
    };
  }, []);
}

/*
* @function MdppAccessibilityPanelGrid
* @description This function returns a NextGenKendoGrid component with the accessibility fixes applied
*/
export function MdppAccessibilityPanelGrid({...rest}: {} & React.ComponentPropsWithoutRef<typeof NextGenKendoGrid>) {
  useToFixAriaRowIndexNan();
  useToEnsureTabIndexSetForGridFilterButton();
  useToAddAriaLabelToPanelGridFilterTextbox();
  return <NextGenKendoGrid {...rest}></NextGenKendoGrid>;
}

/*
* Accessibility - replacing the action button on the view and edit case pages.
* @function cardHeaderIcon
* @description This function returns a div with the specified icon name
*/
export const cardHeaderIcon = (iconName: string, ariaLabel: string, id: string = iconName) : JSX.Element => {
  return <div style={{display:'inline-block', borderRadius:'27px', height:'27px', backgroundColor:'black', width:'27px', color: 'white', textAlign: 'center', verticalAlign:'center'}}>
      <FontIcon id={id} iconName={iconName} aria-label={ariaLabel} />
  </div>
}

/*
* Accessibility fix to ensure tabindex is set for grid column filter button
* @function useToEnsureTabIndexSetForGridFilterButton
* @description This function ensures tabindex is set for grid column filter button
*/
export function useToEnsureTabIndexSetForGridFilterButton() {
  useEffect(() => {
    let gridColumnFilters = document.querySelectorAll(
      ".k-icon.k-i-more-vertical"
    );
    
    const observer = new MutationObserver((mutationsList, observer) => {
      [...gridColumnFilters].filter((filter) => 
        !(filter.parentNode as Element).hasAttribute("tabindex")
      ).forEach((gridFilterButton) => {
        gridFilterButton?.parentElement?.setAttribute("tabindex", "0");
      });
    });

    observer.observe(document.body, { attributes: true });
    
    return () => {
      observer.disconnect();
    }
  }, []);
}

/*
* Accessibility fix to ensure tabindex is set for the panel bar li elements and
* required ARIA attributes (aria-valuenow/min/max) are set on Kendo Splitter splitbars.
* @function useToOverridePanelBarTabIndex
* @description This function ensures proper tab navigation for the panel bar li elements
* and adds the required ARIA value attributes to splitbars with role="separator" (WCAG 4.1.2).
*/
export function useToOverridePanelBarTabIndex(){
  useEffect(() => {

    const applyAccessibilityFixes = () => {
      let panelBars = document.querySelectorAll(".k-panelbar");

      if(panelBars.length === 1){
        let panelBarElements = panelBars[0].querySelectorAll(".k-panelbar .k-panelbar-header");
        (panelBars[0] as HTMLElement).setAttribute("tabindex", "-1");
        panelBarElements.forEach( (liEle) => {
          let li = liEle as HTMLElement;
          li.setAttribute("tabindex", "0");
        });
      }

      // Accessibility (WCAG 4.1.2) - Kendo Splitter splitbars render role="separator"
      // but omit the required aria-valuenow/aria-valuemin/aria-valuemax attributes.
      document.querySelectorAll('.k-splitbar[role="separator"]').forEach((splitbar) => {
        let bar = splitbar as HTMLElement;
        if (!bar.getAttribute("aria-valuenow")) {
          let previousPane = bar.previousElementSibling as HTMLElement | null;
          let flexBasis = previousPane ? (previousPane.style.flexBasis || previousPane.style.width) : "";
          let parsed = flexBasis ? parseFloat(flexBasis) : NaN;
          bar.setAttribute("aria-valuenow", `${isNaN(parsed) ? 50 : Math.round(parsed)}`);
          bar.setAttribute("aria-valuemin", "0");
          bar.setAttribute("aria-valuemax", "100");
        }
      });
    };

    // Run once for elements already present, then watch for elements added later.
    applyAccessibilityFixes();

    const observer = new MutationObserver(() => {
      applyAccessibilityFixes();
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
    }
  }, []);
}

/*
* Accessibility fix so NVDA announces the name and role of the date picker calendar popup (MAS 4.1.2)
* @function useToAddNameAndRoleToCalendarDialog
* @description The Kendo DatePicker calendar popup renders as a portal without a dialog role or an
* accessible name, so NVDA only announces "table table". This observes the DOM and marks exactly one
* calendar as role="dialog" with an accessible name ("Choose date"), so NVDA announces
* "Choose date dialog table ...". The role is set on the .k-calendar element itself (which natively
* carries role="grid"); replacing that grid role removes the extra "table" it would otherwise add,
* leaving only the single inner day-grid table. Because a grid's expanded filter can render more than
* one calendar (first/second condition), only ONE calendar - the one the user is navigating (has a
* focused/selected day) or the first visible one - is labeled, and the marker is stripped from every
* other calendar to prevent duplicate "Choose date dialog" announcements.
* @see WCAG 4.1.2: Name, Role, Value
*/
export function useToAddNameAndRoleToCalendarDialog() {
  useEffect(() => {
    const sessionResourceData = getEntityDataFromSession<any>(SessionStorageConsts.resourceStrings);
    const calendarDialogLabel = getRuleValidationMessage(
      sessionResourceData,
      MessageTypeText.FieldLabelText,
      "ChooseDateDialogAriaLabel",
      UIControlsText.ChooseDateDialogAriaLabel
    );
    // Marker attribute so we only ever revert dialog attributes this hook added, and never
    // touch Kendo's own roles on calendars/wrappers we did not label.
    const dialogMarker = "data-mdpp-cal-dialog";

    const applyDialogRole = () => {
      const calendars = Array.from(document.querySelectorAll<HTMLElement>(".k-calendar"));

      // Clean up dialog attributes left on calendar popup wrappers by a previous version of
      // this fix (it labeled .k-calendar-container instead of the calendar itself). Keeping
      // this makes the fix self-healing across deploys and avoids a stale second dialog.
      document
        .querySelectorAll<HTMLElement>(`.k-calendar-container[${dialogMarker}="true"]`)
        .forEach((wrapper) => {
          wrapper.removeAttribute("aria-label");
          wrapper.removeAttribute("role");
          wrapper.removeAttribute(dialogMarker);
        });

      if (calendars.length === 0) {
        return;
      }

      // Pick the single active calendar: prefer a visible one, and among those the one the
      // user is navigating (has a focused or selected day cell).
      const focusedCellSelector =
        ".k-calendar-td.k-state-focused, .k-calendar-td.k-state-focus, .k-calendar-td.k-selected, .k-calendar-td.k-state-selected, .k-calendar-td[aria-selected='true']";
      const visibleCalendars = calendars.filter((calendar) => calendar.offsetParent !== null);
      const pool = visibleCalendars.length > 0 ? visibleCalendars : calendars;
      const target =
        pool.find((calendar) => calendar.querySelector(focusedCellSelector)) ?? pool[0];

      calendars.forEach((calendar) => {
        if (calendar === target) {
          if (calendar.getAttribute("role") !== "dialog") {
            calendar.setAttribute("role", "dialog");
          }
          if (calendar.getAttribute("aria-label") !== calendarDialogLabel) {
            calendar.setAttribute("aria-label", calendarDialogLabel);
          }
          if (calendar.getAttribute(dialogMarker) !== "true") {
            calendar.setAttribute(dialogMarker, "true");
          }
        } else {
          // Force EVERY other calendar back to Kendo's native grid role and strip our label,
          // so only the single target ever announces as "Choose date dialog". Kendo always
          // renders .k-calendar with role="grid", so restoring role="grid" here is safe and
          // idempotent. This guards against a second open/hidden filter calendar (the expanded
          // grid filter renders first- and second-condition date pickers) being announced as a
          // duplicate dialog.
          if (calendar.getAttribute("aria-label") === calendarDialogLabel) {
            calendar.removeAttribute("aria-label");
          }
          if (calendar.getAttribute("role") === "dialog") {
            calendar.setAttribute("role", "grid");
          }
          if (calendar.getAttribute(dialogMarker) === "true") {
            calendar.removeAttribute(dialogMarker);
          }
        }
      });
    };

    applyDialogRole();

    const observer = new MutationObserver(() => {
      applyDialogRole();
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
    };
  }, []);
}

/**
 * Accessibility fix making the calendar filter popup controls operable with the keyboard (MAS 2.1.1)
 * @function useToMakeCalendarControlsKeyboardAccessible
 * @description In the Kendo DatePicker/filter calendar popup the "TODAY" command renders as a plain
 * <span class="k-nav-today" onClick> and the month/year navigation list renders as <li onClick> items
 * (see kendo-react-dateinputs TodayCommand.js / CalendarNavigationItem.js). Neither is focusable or
 * has a keyboard handler, so a keyboard-only user cannot reach or activate them (only the day grid and
 * the title button are natively operable). This observes the DOM and marks those controls as
 * role="button" with tabindex="0" so they are reachable via Tab and announced as buttons, and a single
 * delegated keydown listener activates them (native click, which fires Kendo's React onClick) on
 * Enter/Space. Delegation on document survives the navigation list's virtualization re-renders, and a
 * marker attribute keeps the attribute writes idempotent so the MutationObserver never loops.
 * @see WCAG 2.1.1: Keyboard (https://www.w3.org/WAI/WCAG21/Understanding/keyboard.html)
 */
export function useToMakeCalendarControlsKeyboardAccessible() {
  useEffect(() => {
    const kbdMarker = "data-mdpp-cal-kbd";
    const selector = ".k-nav-today, .k-calendar-navigation li";

    const markControls = () => {
      document.querySelectorAll<HTMLElement>(selector).forEach((control) => {
        if (control.getAttribute(kbdMarker) === "true") return;
        control.setAttribute(kbdMarker, "true");
        if (control.getAttribute("role") !== "button") {
          control.setAttribute("role", "button");
        }
        if (!control.hasAttribute("tabindex")) {
          control.setAttribute("tabindex", "0");
        }
      });
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Enter" && event.key !== " " && event.key !== "Spacebar") return;
      const target = event.target as Element | null;
      const control = target?.closest<HTMLElement>(".k-nav-today, .k-calendar-navigation li");
      if (!control) return;
      event.preventDefault();
      control.click();
    };

    markControls();
    document.addEventListener("keydown", onKeyDown);

    const observer = new MutationObserver(() => markControls());
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      document.removeEventListener("keydown", onKeyDown);
      document.querySelectorAll<HTMLElement>(`[${kbdMarker}="true"]`).forEach((control) => {
        control.removeAttribute(kbdMarker);
      });
    };
  }, []);
}

export function useToHandleOpenColumnMenuOnKeyboardEnter() {  const filterButtonEnterHandler = useCallback((event: Event) => {
    if((event as KeyboardEvent).key === "Enter"){
      event.preventDefault();
      event.target && (event.target as HTMLElement).click();
    }
  }, []);

  useEffect(() => {
    let filterButtons = document.querySelectorAll('[role="columnheader"] [role="button"][aria-label*="filter"]');
    filterButtons.forEach((filterButton) => {
      filterButton.addEventListener("keydown", filterButtonEnterHandler);
    });
  }, []);

  return () => {
    let filterButtons = document.querySelectorAll('[role="columnheader"] [role="button"][aria-label*="filter"]');
    filterButtons.forEach((filterButton) => {
      filterButton.removeEventListener("keydown", filterButtonEnterHandler);
    });
  }
}

/**
 * Accessibility fix adding an accessible name to the clear search text button in the grid column filter popup
 * @function useToAddAriaLabelToClearSearchButton
 * @description The Kendo checkbox filter popup renders a search box whose clear ("x") button
 * (button.k-button-clear) only contains an icon span with no text, so NVDA announces it as an
 * unnamed "button". This function observes the DOM and sets aria-label="Clear search text" on that
 * button whenever the filter popup is opened, so screen readers announce "Clear search text button".
 * @see WCAG 4.1.2: Ensures every ARIA button, link and menuitem has an accessible name
 */
export function useToAddAriaLabelToClearSearchButton() {
  useEffect(() => {
    const setClearSearchLabel = () => {
      document
        .querySelectorAll(".k-searchbox button.k-button-clear:not([aria-label])")
        .forEach((clearButton) => {
          clearButton.setAttribute("aria-label", "Clear search text");
        });
    };

    setClearSearchLabel();

    const observer = new MutationObserver(() => {
      setClearSearchLabel();
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
    };
  }, []);
}

/**
 * Accessibility fix announcing the filtered result count in the grid column checkbox filter popup.
 * @function useToAnnounceFilterCheckboxSearchResults
 * @description The Kendo GridColumnMenuCheckboxFilter renders a search box that filters the list of
 * checkbox options, but the resulting count is a silent visual-only change, so NVDA/JAWS never report
 * how many options matched the typed text. This hook observes the DOM for the checkbox filter popup,
 * listens to the search box input, and announces the number of matching options (e.g. "1 record found",
 * "No results found") through the shared aria-live region, with no visual or focus impact. The count is
 * read after a short debounce so Kendo has re-rendered the filtered list (ul.k-multicheck-wrap), and the
 * always-present "Check All" row (li.k-check-all-wrap) is excluded.
 * @see WCAG 4.1.3: Status Messages (https://www.w3.org/WAI/WCAG21/Understanding/status-messages.html)
 */
export function useToAnnounceFilterCheckboxSearchResults() {
  useEffect(() => {
    const sessionResourceData = getEntityDataFromSession<any>(SessionStorageConsts.resourceStrings);
    let debounceId: ReturnType<typeof setTimeout> | undefined;

    const announceCount = (input: Element) => {
      const container = input.closest(".k-filter-menu-container");
      const wrap = container?.querySelector("ul.k-multicheck-wrap");
      if (!wrap) return;
      const optionCount = wrap.querySelectorAll("li.k-item:not(.k-check-all-wrap)").length;
      const message =
        optionCount === 0
          ? getRuleValidationMessage(sessionResourceData, MessageTypeText.FieldLabelText, "GridSearchNoResultsFoundAnnouncement", UIControlsText.GridSearchNoResultsFoundAnnouncement)
          : optionCount === 1
            ? getRuleValidationMessage(sessionResourceData, MessageTypeText.FieldLabelText, "GridSearchSingleResultFoundAnnouncement", UIControlsText.GridSearchSingleResultFoundAnnouncement)
            : getRuleValidationMessage(sessionResourceData, MessageTypeText.FieldLabelText, "GridSearchResultsFoundAnnouncement", UIControlsText.GridSearchResultsFoundAnnouncement).replace("{0}", `${optionCount}`);
      announceToScreenReader(message, "polite");
    };

    const onSearchInput = (ev: Event) => {
      const input = ev.currentTarget as Element;
      if (debounceId) clearTimeout(debounceId);
      // Wait for Kendo to re-render the filtered option list after the keystroke.
      debounceId = setTimeout(() => announceCount(input), 400);
    };

    const bindSearchInputs = () => {
      document
        .querySelectorAll(".k-filter-menu-container .k-searchbox input:not([data-mdpp-search-announce])")
        .forEach((input) => {
          input.setAttribute("data-mdpp-search-announce", "true");
          input.addEventListener("input", onSearchInput);
        });
    };

    bindSearchInputs();

    const observer = new MutationObserver(() => bindSearchInputs());
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      if (debounceId) clearTimeout(debounceId);
      document
        .querySelectorAll(".k-filter-menu-container .k-searchbox input[data-mdpp-search-announce]")
        .forEach((input) => {
          input.removeEventListener("input", onSearchInput);
          input.removeAttribute("data-mdpp-search-announce");
        });
    };
  }, []);
}

/**
 * Accessibility fix for CoherenceHeader rightItemsContainer menubar role
 * @function useToFixHeaderMenubarRole
 * @description The CoherenceHeader third-party component renders a div with role="menubar"
 * whose direct children are buttons with role="menuitem" (Share Feedback, profile icon, notifications).
 * These are not real menu semantics, so both role="menubar" and role="menuitem" are removed to resolve:
 *   - WCAG 1.3.1 aria-required-children: menubar must contain menuitem children
 *   - WCAG 1.3.1 aria-required-parent: menuitem must be inside menu, menubar or group
 * @see WCAG 1.3.1: Ensures elements with an ARIA role that require child roles contain them
 * @see WCAG 1.3.1: Ensures elements with an ARIA role that require parent roles are contained by them
 * @see https://accessibilityinsights.io/info-examples/web/aria-required-children
 * @see https://accessibilityinsights.io/info-examples/web/aria-required-parent
 */
export function useToFixHeaderMenubarRole() {
  useEffect(() => {
    const menubar = document.querySelector('[role="menubar"]');
    if (menubar) {
      menubar.removeAttribute('role');
      menubar.querySelectorAll('[role="menuitem"]').forEach((item) => {
        item.removeAttribute('role');
      });
    }
  }, []);
}

/**
 * Accessibility fix providing correct role, state and accessible name for the grid column
 * filter menu combo boxes and edit fields.
 * @function useToFixFilterMenuComboboxAccessibility
 * @description The Kendo GridColumnMenuFilter renders its operator/logic selectors as DropDownList
 * components whose wrapper is a <span role="listbox" aria-haspopup="true"> with no accessible name,
 * and its value editor as a bare <input class="k-textbox"> with no label. As a result NVDA announces
 * them as "list sub menu" and "edit blank" instead of a named combo box and edit field, and no
 * expanded/collapsed state is conveyed.
 *
 * This hook:
 *   - records the column title whenever a column filter menu is activated (via the column menu icon),
 *   - converts each filter DropDownList wrapper to role="combobox" with aria-haspopup="listbox" and
 *     aria-label="Filter {column}", so NVDA announces e.g. "Filter Case ID combo box Contains
 *     collapsed opens list",
 *   - adds aria-label="Filter {column}" to the filter value edit fields.
 *
 * Kendo re-renders these wrappers on open/close (resetting role and aria-* attributes), so the
 * attributes are re-applied through a MutationObserver, mirroring the existing filter-popup
 * accessibility hooks. Only presentational ARIA attributes are changed, so the underlying Kendo
 * keyboard/selection behaviour is unaffected.
 * @see WCAG 4.1.2: Name, Role, Value (https://www.w3.org/WAI/WCAG21/Understanding/name-role-value.html)
 */
export function useToFixFilterMenuComboboxAccessibility() {
  useEffect(() => {
    const popupSelector = ".k-grid-columnmenu-popup";
    let lastColumnTitle = "";

    const recordColumnTitle = (event: Event) => {
      const target = event.target as HTMLElement;
      const menuAnchor = target?.closest?.(".k-grid-column-menu");
      if (!menuAnchor) {
        return;
      }
      const columnHeader = menuAnchor.closest('th[role="columnheader"]');
      const titleSpan = columnHeader?.querySelector(".k-column-title");
      const title = (titleSpan?.textContent || columnHeader?.textContent || "").trim();
      if (title) {
        lastColumnTitle = title;
      }
    };

    const fixFilterMenuAccessibility = () => {
      if (!lastColumnTitle) {
        return;
      }
      const label = `Filter ${lastColumnTitle}`;
      document.querySelectorAll(popupSelector).forEach((popup) => {
        // Operator and logic selectors -> named combo boxes with correct popup semantics
        popup.querySelectorAll(".k-dropdown-wrap").forEach((wrap) => {
          if (wrap.getAttribute("role") !== "combobox") {
            wrap.setAttribute("role", "combobox");
          }
          if (wrap.getAttribute("aria-haspopup") !== "listbox") {
            wrap.setAttribute("aria-haspopup", "listbox");
          }
          if (wrap.getAttribute("aria-label") !== label) {
            wrap.setAttribute("aria-label", label);
          }
        });
        // Value edit fields (text / numeric / date) -> named edit fields
        popup.querySelectorAll("input:not([aria-hidden='true'])").forEach((input) => {
          if (!input.getAttribute("aria-label")) {
            input.setAttribute("aria-label", label);
          }
        });
      });
    };

    document.addEventListener("click", recordColumnTitle, true);
    document.addEventListener("keydown", recordColumnTitle, true);

    fixFilterMenuAccessibility();

    const observer = new MutationObserver(() => {
      fixFilterMenuAccessibility();
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["role", "aria-haspopup", "aria-label", "aria-expanded"],
    });

    return () => {
      observer.disconnect();
      document.removeEventListener("click", recordColumnTitle, true);
      document.removeEventListener("keydown", recordColumnTitle, true);
    };
  }, []);
}

// Id of the single global visually-hidden aria-live region used for status messages.
export const ScreenReaderAnnouncerId = "mdppScreenReaderAnnouncer";

/**
 * Resolve the correct DOM host for the shared aria-live region.
 * @function getScreenReaderAnnouncerHost
 * @description A live region placed on document.body is ignored by NVDA/JAWS when a modal dialog
 * (aria-modal="true", e.g. a Fluent Panel/Dialog such as the Reseller/Distributor lookups) is open,
 * because assistive tech treats everything outside the modal subtree as inert. To keep status
 * announcements audible inside those dialogs, the region must live inside the top-most open modal.
 * Falls back to document.body when no modal is open (the normal page case).
 */
function getScreenReaderAnnouncerHost(): HTMLElement {
  const candidates = Array.from(
    document.querySelectorAll<HTMLElement>('[aria-modal="true"], .ms-Panel-main, .ms-Dialog-main')
  ).filter((el) => el.getClientRects().length > 0);
  return candidates.length ? candidates[candidates.length - 1] : document.body;
}

/**
 * Accessibility fix (WCAG 2.1 SC 4.1.3 Status Messages).
 * @function announceToScreenReader
 * @description Several actions across the application (file upload/delete, grid refresh,
 * form reset, etc.) update the UI without moving focus, so NVDA/JAWS never announce the
 * resulting status message. This helper pushes text into a single global visually-hidden
 * aria-live region so screen readers announce the status change with no visual or focus impact.
 * The region is cleared first so repeated/identical messages are re-announced reliably.
 * @param {string} message the status text to announce (e.g. "Test.png file uploaded").
 * @param {"polite" | "assertive"} politeness aria-live politeness, defaults to "assertive".
 */
export function ensureScreenReaderAnnouncer(): HTMLElement | null {
  if (typeof document === "undefined") {
    return null;
  }
  let liveRegion = document.getElementById(ScreenReaderAnnouncerId);
  if (!liveRegion) {
    liveRegion = document.createElement("div");
    liveRegion.id = ScreenReaderAnnouncerId;
    liveRegion.setAttribute("aria-atomic", "true");
    liveRegion.setAttribute("role", "status");
    liveRegion.setAttribute("aria-live", "assertive");
    // Visually hidden but still available to assistive technology.
    Object.assign(liveRegion.style, {
      position: "absolute",
      width: "1px",
      height: "1px",
      padding: "0",
      margin: "-1px",
      overflow: "hidden",
      clip: "rect(0, 0, 0, 0)",
      whiteSpace: "nowrap",
      border: "0",
    });
  }
  // Keep the region inside the top-most open modal dialog so NVDA/JAWS still announce status
  // messages there (a body-level live region is ignored under aria-modal); otherwise host on body.
  const host = getScreenReaderAnnouncerHost();
  if (liveRegion.parentElement !== host) {
    host.appendChild(liveRegion);
  }
  return liveRegion;
}

/**
 * Accessibility hook to pre-mount the shared aria-live region on component mount.
 * @function useScreenReaderAnnouncer
 * @description NVDA/JAWS only reliably announce updates to a live region that already existed
 * in the DOM before its text changed. Creating the region lazily on the first
 * announceToScreenReader call means the very first status message (e.g. the first column-header
 * sort) is often missed. Mounting the region up front guarantees the first announcement is read.
 */
export function useScreenReaderAnnouncer(): void {
  useEffect(() => {
    ensureScreenReaderAnnouncer();
  }, []);
}

export function announceToScreenReader(
  message: string,
  politeness: "polite" | "assertive" = "assertive",
  delayMs: number = 100
): void {
  if (!message || typeof document === "undefined") {
    return;
  }

  const liveRegion = ensureScreenReaderAnnouncer();
  if (!liveRegion) {
    return;
  }
  liveRegion.setAttribute("aria-live", politeness);

  // Clear first so an identical, consecutive message is still announced by NVDA.
  liveRegion.textContent = "";
  window.setTimeout(() => {
    // Re-resolve the host and region at text-set time (not just at call time). Some announcements are
    // triggered while a modal Panel/Dialog is closing (e.g. adding a reseller from the lookup panel):
    // at call time getScreenReaderAnnouncerHost() would parent the region inside that panel, which
    // then unmounts on close and discards the pending message, leaving NVDA silent. Re-running
    // ensureScreenReaderAnnouncer() here re-parents the single region to the correct current host
    // (document.body once the panel has gone, or the top-most modal when one is genuinely still open),
    // so the message is announced reliably in both cases.
    const region = ensureScreenReaderAnnouncer();
    if (region) {
      region.setAttribute("aria-live", politeness);
      region.textContent = message;
    }
  }, delayMs);
}

/**
 * @constant FOCUS_INVALID_FIELD_SELECTOR
 * @description Matches controls Fluent has already flagged as invalid (an error message is shown).
 * Besides aria-invalid (set by TextField/Dropdown when an errorMessage is present), the Fluent
 * error state class is matched as a fallback for versions/controls that render the error styling
 * without toggling aria-invalid on the focusable element.
 */
const FOCUS_INVALID_FIELD_SELECTOR = [
  '[aria-invalid="true"]',
  ".ms-TextField--error input",
  ".ms-TextField--error textarea",
].join(",");

/**
 * @constant FOCUS_REQUIRED_FIELD_SELECTOR
 * @description Matches required controls that may still be empty when Submit is pressed. In the bug
 * repro the user tabs straight to Submit without ever touching a field, so Fluent never runs its
 * per-field validation (fields use validateOnFocusOut/validateOnLoad={false}) and never sets
 * aria-invalid. We therefore also look for the first *required and empty* control. Fluent v7 marks
 * required TextField/Dropdown wrappers with the `is-required` state class, and required Dropdowns
 * expose aria-required="true" on their div[role='combobox']. The selectors below defensively cover
 * each rendered shape so the helper is resilient to the exact Fluent markup.
 */
const FOCUS_REQUIRED_FIELD_SELECTOR = [
  ".ms-TextField.is-required input",
  ".ms-TextField.is-required textarea",
  ".ms-Dropdown.is-required [role='combobox']",
  ".ms-Dropdown-container.is-required [role='combobox']",
  ".ms-ComboBox.is-required input",
  ".ms-ComboBox.is-required [role='combobox']",
  "input[aria-required='true']",
  "textarea[aria-required='true']",
  "[role='combobox'][aria-required='true']",
  "input[required]",
  "textarea[required]",
].join(",");

/**
 * @function focusFirstInvalidField
 * @description Accessibility fix (MAS 2.4.3 Focus Order). When a form is submitted with required
 * fields missing/invalid, validation stays visual: the error message bar appears and the inline
 * "This is required field" text is shown, but keyboard focus is left on the Submit button. Screen
 * reader users are therefore never taken to the control that needs their attention. This helper
 * moves focus to the first field that needs attention so its label and required state are
 * announced, while the (aria-live) error MessageBar announces the status message.
 *
 * A field is considered a target when it is either already flagged invalid (aria-invalid) or is
 * required and still empty. The empty case is essential: in the primary repro the user never
 * focuses the field, so Fluent (validateOnFocusOut / validateOnLoad={false}) never sets
 * aria-invalid — matching only aria-invalid would find nothing and focus would stay on Submit.
 *
 * Because submission can switch to the tab that owns the first error and re-render asynchronously
 * (inactive tabs are unmounted), the target is briefly polled for before focus is applied. The
 * helper only changes focus (and scrolls the field into view); it never alters data or validation.
 * @param {object} [options] optional selector overrides (mainly for tests).
 * @param {string} [options.invalidSelector] selector for already-invalid controls.
 * @param {string} [options.requiredSelector] selector for required controls that may be empty.
 * @param {HTMLElement|null} [options.container] limit the search to this element's subtree.
 * @param {string} [options.containerSelector] limit the search to the first element matching this
 * selector. Use when the Submit lives inside a dialog/panel (e.g. the Add New Customer dialog) so
 * focus lands on the dialog's first error field instead of an underlying required field on the page.
 * @param {string} [options.fallbackSelector] selector for a validation summary/status element to
 * focus when no specific input field can be located. Required items on some pages are grids or
 * data selections (e.g. "add at least one Legal Entity") rather than Fluent input controls, so no
 * aria-invalid / required-empty field exists to receive focus. Moving focus to the visible error
 * summary still satisfies MAS 2.4.3 (focus leaves Submit) and lets the screen reader announce the
 * status message. The element is made programmatically focusable (tabindex=-1) if needed.
 * @see WCAG 2.4.3 Focus Order (https://www.w3.org/WAI/WCAG21/Understanding/focus-order.html)
 */
export function focusFirstInvalidField(options?: {
  invalidSelector?: string;
  requiredSelector?: string;
  container?: HTMLElement | null;
  containerSelector?: string;
  fallbackSelector?: string;
}): void {
  if (typeof document === "undefined") {
    return;
  }
  const invalidSelector = options?.invalidSelector ?? FOCUS_INVALID_FIELD_SELECTOR;
  const requiredSelector = options?.requiredSelector ?? FOCUS_REQUIRED_FIELD_SELECTOR;
  const maxAttempts = 20; // ~2s total (20 x 100ms) to allow the tab switch/re-render to settle.
  let attempts = 0;
  let stickinessRetries = 0;
  const maxStickinessRetries = 6; // ~1.5s guard against async re-renders stealing the moved focus.

  // Resolve the search root on every attempt: a dialog/panel container may only mount a tick after
  // Submit is pressed. Falls back to the whole document when no (or a not-yet-rendered) container.
  const getRoot = (): ParentNode => {
    if (options?.container) {
      return options.container;
    }
    if (options?.containerSelector) {
      const scoped = document.querySelector<HTMLElement>(options.containerSelector);
      if (scoped) {
        return scoped;
      }
    }
    return document;
  };

  const isVisible = (el: HTMLElement): boolean =>
    el.offsetParent !== null &&
    !el.hasAttribute("disabled") &&
    el.getAttribute("aria-disabled") !== "true" &&
    el.getAttribute("aria-hidden") !== "true";

  const isEmptyValue = (el: HTMLElement): boolean => {
    const valued = el as HTMLInputElement | HTMLTextAreaElement;
    if (typeof valued.value === "string") {
      return valued.value.trim().length === 0;
    }
    // Fluent Dropdown/ComboBox render the selection as visible text; empty when none is selected.
    if (el.getAttribute("role") === "combobox") {
      // Fluent Dropdown: when nothing is selected the title span carries the placeholder modifier
      // class (ms-Dropdown-titleIsPlaceHolder) and its text is just the placeholder — so textContent
      // is NOT a reliable "has value" signal (it would look filled). Detect the placeholder class,
      // and also treat empty title text as empty. aria-activedescendant is only set while the list is
      // open, so it cannot be used to infer a selection here.
      const dropdownTitle = el.querySelector<HTMLElement>(".ms-Dropdown-title");
      if (dropdownTitle) {
        return (
          dropdownTitle.classList.contains("ms-Dropdown-titleIsPlaceHolder") ||
          (dropdownTitle.textContent?.trim().length ?? 0) === 0
        );
      }
      // Fluent ComboBox exposes its editable value on an inner input.
      const comboInput = el.querySelector<HTMLInputElement>(".ms-ComboBox-Input, input");
      if (comboInput && typeof comboInput.value === "string") {
        return comboInput.value.trim().length === 0;
      }
      return false;
    }
    return false;
  };

  const isTarget = (el: HTMLElement): boolean => {
    if (!isVisible(el)) {
      return false;
    }
    // Already-invalid controls are always a target; required controls only when still empty.
    return el.matches(invalidSelector) || isEmptyValue(el);
  };

  // Applies focus to a candidate and confirms it actually landed. Some matched controls are not
  // directly focusable (e.g. a Fluent Dropdown's inner div[role='combobox'] that delegates focus to
  // a sibling, or a control briefly covered while its tab re-renders), so a bare focus() silently
  // no-ops and would leave keyboard focus stranded on the Submit button. When the first focus() does
  // not stick we make the element programmatically focusable (tabindex=-1) and retry once, so the
  // reported symptom — focus staying on Submit — cannot happen when a target exists.
  const focusTarget = (target: HTMLElement): boolean => {
    target.focus();
    if (document.activeElement !== target) {
      if (!target.hasAttribute("tabindex")) {
        target.setAttribute("tabindex", "-1");
      }
      target.focus();
    }
    if (document.activeElement === target) {
      if (typeof target.scrollIntoView === "function") {
        target.scrollIntoView({ block: "center" });
      }
      scheduleStickinessGuard();
      return true;
    }
    return false;
  };

  // After focus is moved to a field, an asynchronous re-render (e.g. the reseller EditableGrid
  // finishing its data load after a tab switch, which remounts its cells) can reset keyboard focus
  // back to <body> or the Submit button. Briefly re-acquire the first outstanding target if that
  // happens so focus reliably rests on the error field, not on Submit.
  const scheduleStickinessGuard = (): void => {
    if (stickinessRetries >= maxStickinessRetries) {
      return;
    }
    window.setTimeout(() => {
      const active = document.activeElement as HTMLElement | null;
      const focusLost = !active || active === document.body || active.tagName === "BUTTON";
      if (!focusLost) {
        return; // Focus is on the field (or another field the user is now interacting with).
      }
      stickinessRetries++;
      const nextTargets = Array.from(
        getRoot().querySelectorAll<HTMLElement>(`${invalidSelector},${requiredSelector}`)
      ).filter(isTarget);
      for (const next of nextTargets) {
        if (focusTarget(next)) {
          return;
        }
      }
      scheduleStickinessGuard();
    }, 250);
  };

  const tryFocus = (): void => {
    // DOM-ordered pass so the first field needing attention wins, regardless of category. We iterate
    // every current target (not just the first) so that if the first one cannot receive focus this
    // tick we fall through to the next candidate instead of giving up and leaving focus on Submit.
    const targets = Array.from(
      getRoot().querySelectorAll<HTMLElement>(`${invalidSelector},${requiredSelector}`)
    ).filter(isTarget);
    for (const target of targets) {
      if (focusTarget(target)) {
        return;
      }
    }
    if (++attempts < maxAttempts) {
      window.setTimeout(tryFocus, 100);
      return;
    }
    // No focusable field was found within the poll window. On pages whose required items are
    // grids/data selections (e.g. Legal Entities) there is no input to focus, so fall back to the
    // validation summary: focus leaves Submit (MAS 2.4.3) and the screen reader reads the status.
    focusFallback();
  };

  const focusFallback = (): void => {
    if (!options?.fallbackSelector) {
      return;
    }
    const fallback = document.querySelector<HTMLElement>(options.fallbackSelector);
    if (fallback && isVisible(fallback)) {
      if (!fallback.hasAttribute("tabindex")) {
        fallback.setAttribute("tabindex", "-1");
      }
      fallback.focus();
      if (typeof fallback.scrollIntoView === "function") {
        fallback.scrollIntoView({ block: "center" });
      }
    }
  };

  // Defer so the invalid state (and any tab switch) has a chance to render first.
  window.setTimeout(tryFocus, 100);
}

/**
 * Moves keyboard focus to a sibling Delete button after a grid/list row is removed.
 * @function focusDeleteButtonAfterRemoval
 * @description Accessibility fix (WCAG/MAS 2.4.3 Focus Order). When a row is deleted its Delete
 * button is removed from the DOM and the browser drops keyboard focus to <body> ("document blank"),
 * so NVDA falls silent and the user loses their place. This moves focus to the Delete button that now
 * occupies the deleted row's slot (or the last remaining row when the final row was deleted), so the
 * newly focused, uniquely-named Delete button is announced. Each row's Delete button id must be
 * `${buttonIdPrefix}${rowId}`. The deleted row's position is captured synchronously (before React
 * re-renders and removes it); the DOM is then polled until the button count drops, at which point
 * focus is applied. If no focusable Delete button remains (all rows removed) it does nothing, leaving
 * the existing aria-live deletion announcement to convey the status.
 * @param {object} options
 * @param {string} options.buttonIdPrefix id prefix shared by every row's Delete button.
 * @param {string} options.deletedRowId row id of the button being removed (its id = prefix + rowId).
 * @see WCAG 2.4.3: Focus Order
 */
export function focusDeleteButtonAfterRemoval(options: {
  buttonIdPrefix: string;
  deletedRowId: string;
}): void {
  if (typeof document === "undefined" || !options?.buttonIdPrefix) {
    return;
  }
  const { buttonIdPrefix, deletedRowId } = options;
  const selector = `[id^="${buttonIdPrefix.replace(/"/g, '\\"')}"]`;
  const isFocusable = (el: HTMLElement): boolean =>
    el.offsetParent !== null &&
    !el.hasAttribute("disabled") &&
    el.getAttribute("aria-disabled") !== "true";

  // Snapshot the focusable Delete buttons BEFORE the re-render removes the deleted row's button,
  // so the deleted row's position (and hence which row becomes "next") is known.
  const before = Array.from(document.querySelectorAll<HTMLElement>(selector)).filter(isFocusable);
  const previousCount = before.length;
  const foundIndex = before.findIndex((el) => el.id === `${buttonIdPrefix}${deletedRowId}`);
  const deletedIndex = foundIndex >= 0 ? foundIndex : Math.max(0, previousCount - 1);

  let attempts = 0;
  const maxAttempts = 20; // ~2s (20 x 100ms) for the grid re-render to settle.

  const tryFocus = (): void => {
    const buttons = Array.from(document.querySelectorAll<HTMLElement>(selector)).filter(isFocusable);
    // Wait until the row has actually been removed (button count dropped) before moving focus.
    if (buttons.length >= previousCount && ++attempts < maxAttempts) {
      window.setTimeout(tryFocus, 100);
      return;
    }
    if (buttons.length > 0) {
      const target = buttons[Math.min(deletedIndex, buttons.length - 1)];
      target.focus();
      if (typeof target.scrollIntoView === "function") {
        target.scrollIntoView({ block: "center" });
      }
    }
  };

  window.setTimeout(tryFocus, 100);
}

/**
 * Accessibility fix adding an accessible name to the empty hierarchy (row-detail) column header button.
 * @function useToAddAriaLabelToHierarchyHeaderButton
 * @description When a Kendo grid renders a detail template, it adds a leading hierarchy column whose
 * header cell (th.k-hierarchy-cell) contains a sortable link (span.k-link[role="button"]) with an empty
 * .k-column-title. That button therefore has no accessible name, so NVDA announces an unnamed "button"
 * and axe reports aria-command-name. This hook observes the DOM and sets aria-label="Row details" on that
 * empty hierarchy header button only, leaving all other column headers (which already expose their title)
 * untouched. It is purely additive to the DOM and does not change sorting/expand behaviour.
 * @see WCAG 4.1.2: Ensure every ARIA button, link and menuitem has an accessible name (aria-command-name)
 */
export function useToAddAriaLabelToHierarchyHeaderButton() {
  useEffect(() => {
    const sessionResourceData = getEntityDataFromSession<any>(SessionStorageConsts.resourceStrings);
    const hierarchyHeaderLabel = getRuleValidationMessage(
      sessionResourceData,
      MessageTypeText.FieldLabelText,
      "GridRowDetailsColumnHeader",
      UIControlsText.GridRowDetailsColumnHeader
    );

    const applyLabel = () => {
      document
        .querySelectorAll('.k-hierarchy-cell .k-link[role="button"]')
        .forEach((headerButton) => {
          // Only label the hierarchy header button when it has no visible/accessible name of its own.
          const columnTitle = headerButton.querySelector(".k-column-title")?.textContent?.trim();
          const hasText = (headerButton.textContent?.trim().length ?? 0) > 0;
          if (!columnTitle && !hasText) {
            if (headerButton.getAttribute("aria-label") !== hierarchyHeaderLabel) {
              headerButton.setAttribute("aria-label", hierarchyHeaderLabel);
            }
          }
        });
    };

    applyLabel();

    // Kendo re-renders header cells on sort/filter/reorder/data load; re-apply so the label persists.
    let scheduled = false;
    const observer = new MutationObserver(() => {
      if (scheduled) {
        return;
      }
      scheduled = true;
      window.requestAnimationFrame(() => {
        scheduled = false;
        applyLabel();
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
    };
  }, []);
}
